import React, { useEffect, useState, useRef } from 'react';
import styles from './WindguruWidget.module.scss';
import Loader from '../../UI/Loader/Loader';

declare global {
  interface Window {
    WgWidget?: any;
  }
}

interface WindguruWidgetProps {
  stationId: string;
  widgetId: string;
  additionalParams?: Record<string, string | number | boolean>;
  title?: string;
}

const WindguruWidget: React.FC<WindguruWidgetProps> = ({ stationId, widgetId, additionalParams = {}, title = 'Wind Forecast' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const widgetRef = useRef<HTMLDivElement>(null);

  // Completely clean up any existing widget
  const cleanupWidget = () => {
    // Remove any existing widget script
    const existingScript = document.getElementById(`windguru-script-${widgetId}`);
    if (existingScript) {
      existingScript.remove();
    }
    
    // Clear the widget container
    if (widgetRef.current) {
      widgetRef.current.innerHTML = '';
    }
    
    // Remove any global widget instances that might conflict
    if (typeof window !== 'undefined' && window.WgWidget) {
      // This helps prevent conflicts between multiple widget instances
      const widgetInstance = window.WgWidget[widgetId];
      if (widgetInstance) {
        delete window.WgWidget[widgetId];
      }
    }
  };

  useEffect(() => {
    // First cleanup any existing widget
    cleanupWidget();
    setIsLoading(true);

    const loadWidget = () => {
      const defaultParams = {
        s: stationId,
        m: "100",
        uid: widgetId,
        wj: "kmh",
        tj: "c",
        waj: "m",
        tij: "m",
        odh: "0",
        doh: "24",
        fhours: "168",
        hrsm: "1",
        vt: "forecasts",
        lng: "en",
        idbs: "1",
        p: "WINDSPD,GUST,SMER,TMPE,TCDC,APCP1s"
      };

      const mergedParams = { ...defaultParams, ...additionalParams };
      const params = Object.entries(mergedParams).map(([key, value]) => `${key}=${value}`);

      const script = document.createElement('script');
      script.id = `windguru-script-${widgetId}`;
      script.src = "https://www.windguru.cz/js/widget.php?" + params.join("&");
      script.async = true;
      
      script.onload = () => {
        setIsLoading(false);
      };
      
      script.onerror = (error) => {
        console.error('Windguru widget failed to load:', error);
        setIsLoading(false);
      };

      document.body.appendChild(script);
    };

    if (typeof window !== 'undefined') {
      // Allow a small delay to ensure proper cleanup
      const timer = setTimeout(() => {
        loadWidget();
      }, 100);

      return () => {
        clearTimeout(timer);
        cleanupWidget();
      };
    }

    return cleanupWidget;
  }, [stationId, widgetId, JSON.stringify(additionalParams)]);

  return (
    <div className={styles.widgetContainer}>
      <div className={styles.widgetHeader}>
        <h6>{title}</h6>
      </div>
      {isLoading && (
        <div className={styles.loading}>
          <Loader size="large" />
        </div>
      )}
      <div 
        id={widgetId} 
        ref={widgetRef} 
        className={styles.widgetContent} 
      />
    </div>
  );
};

export default WindguruWidget;
